import { logEvent } from "./analytics";

interface ErrorWithMessage {
  message: string;
  stack?: string;
  code?: string;
  name?: string;
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}

export function setupErrorTracking() {
  // Set up global error handler
  window.onerror = (message, source, lineno, colno, error) => {
    const errorInfo = {
      message,
      source,
      lineno,
      colno,
      stack: error?.stack,
    };

    logEvent('error_occurred', {
      error_type: 'uncaught_error',
      error_message: String(message),
      error_stack: error?.stack,
      source,
      line: lineno,
      column: colno,
    });

    console.error('Global error:', errorInfo);
    return false;
  };

  // Set up unhandled promise rejection handler
  window.onunhandledrejection = (event) => {
    const error = event.reason;
    const errorInfo = {
      message: getErrorMessage(error),
      stack: error?.stack,
    };

    logEvent('error_occurred', {
      error_type: 'unhandled_promise_rejection',
      error_message: errorInfo.message,
      error_stack: error?.stack,
    });

    console.error('Unhandled Promise Rejection:', errorInfo);
  };
}

export function trackError(error: unknown, context?: Record<string, unknown>) {
  const errorInfo = {
    message: getErrorMessage(error),
    stack: error instanceof Error ? error.stack : undefined,
    context,
  };

  logEvent('error_occurred', {
    error_type: 'tracked_error',
    error_message: errorInfo.message,
    error_stack: errorInfo.stack,
    ...context,
  });

  console.error('Tracked error:', errorInfo);
} 